/**
 * Shared Europe/Dublin wall-clock ⇄ instant helpers used by both
 * `lateness.ts` (minutes-late) and `off-schedule.ts` (off-schedule note).
 */

/**
 * The UTC offset, in minutes, that Europe/Dublin observes at the given
 * instant (0 for GMT, 60 for Irish Summer Time). Read off `Intl` rather than
 * computed from the DST transition rules, so it can't drift from whatever
 * the ICU tz database says is actually in effect.
 */
function dublinOffsetMinutes(instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Dublin',
    timeZoneName: 'shortOffset',
  }).formatToParts(instant);
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+0';
  // e.g. "GMT+1" or "GMT" (GMT with no suffix means +0).
  const match = /GMT([+-]\d+)?/.exec(tzName);
  const hours = match?.[1] ? Number(match[1]) : 0;
  return hours * 60;
}

/**
 * Builds the instant (epoch ms) corresponding to `HH:MM` wall-clock time on
 * `YYYY-MM-DD` in Europe/Dublin.
 *
 * Two-pass: first guess the offset using midday UTC on that calendar date
 * (safely inside the same Dublin day regardless of DST), then apply that
 * offset to the target wall-clock time. A single DST transition happens at
 * 01:00 Dublin time, so the midday guess always reflects the offset that's
 * actually in effect for the requested HH:MM on that date.
 */
export function dublinDateTimeMs(dateStr: string, hhmm: string): number {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = hhmm.split(':').map(Number);
  const middayUtcGuess = Date.UTC(year, month - 1, day, 12, 0, 0);
  const offsetMinutes = dublinOffsetMinutes(new Date(middayUtcGuess));
  return Date.UTC(year, month - 1, day, hour, minute, 0) - offsetMinutes * 60_000;
}
